// What every seat did over a match, counted as it happened, and the end screen's answers
// read off it: the board's awards and each phone's own numbers.
//
// The counters ride on the seat in the synced state — every device has to be able to
// paint the end screen, and only the device that acted saw the action happen — so they
// arrive through the same untrusted door as everything else and Player.normalize runs
// them through normalizeStats.

// The ones every player gets shown first; the rest only once they have happened.
const BASIC_ROWS = [
  ["drunk", "Klunkar druckna"],
  ["dealt", "Klunkar utdelade"],
  ["healed", "Klunkar återställda"],
  ["blocked", "Klunkar stoppade"],
  ["played", "Kort spelade"],
  // Drawn by a card or a minion. The turn's own draw is the same for everyone and
  // would drown the difference.
  ["drawn", "Extra kort dragna"],
];
const EXTRA_ROWS = [
  ["targeted", "Kort riktade mot dig"],
  ["manaSpent", "Mana spenderad"],
  ["summoned", "Vakter utplacerade"],
  ["minionKills", "Vakter utslagna"],
  ["knockouts", "Spelare utslagna"],
  ["stolen", "Kort tagna"],
  ["selfSips", "Klunkar från egna kort"],
  ["skipped", "Överhoppade turer"],
  ["fatigue", "Klunkar av tom lek"],
];

export const STAT_KEYS = [...BASIC_ROWS, ...EXTRA_ROWS].map(([key]) => key);

const KNOWN = new Set(STAT_KEYS);

function count(value) {
  const number = Math.trunc(Number(value));
  return Number.isFinite(number) && number > 0 ? number : 0;
}

export function freshStats() {
  return Object.fromEntries(STAT_KEYS.map((key) => [key, 0]));
}

// Known counters only, each a whole number from zero up: a forged blob can neither
// invent a statistic nor hand itself a negative or infinite one.
export function normalizeStats(value) {
  const source = value && typeof value === "object" ? value : {};
  return Object.fromEntries(STAT_KEYS.map((key) => [key, count(source[key])]));
}

export function addStat(player, key, amount = 1) {
  const step = count(amount);
  if (!player || !KNOWN.has(key) || !step) return;
  player.stats = normalizeStats(player.stats);
  player.stats[key] += step;
}

function statOf(player, key) {
  return count(player?.stats?.[key]);
}

// The board's awards, in the order the room cares about them: the two drinking ones the
// whole table is waiting for, then healing and guarding, then the rest. `best: "min"` is
// the one award where zero is the best possible answer rather than no answer at all.
const AWARDS = [
  {id: "most-drunk", key: "drunk", icon: "🍺", title: "Mest klunkar", unit: ["klunk", "klunkar"]},
  {id: "least-drunk", key: "drunk", icon: "😇", title: "Minst klunkar", unit: ["klunk", "klunkar"], best: "min"},
  {id: "healed", key: "healed", icon: "💧", title: "Återställt sig mest", unit: ["klunk bort", "klunkar bort"]},
  {id: "blocked", key: "blocked", icon: "🛡️", title: "Skyddat sig mest", unit: ["klunk stoppad", "klunkar stoppade"]},
  {id: "targeted", key: "targeted", icon: "🎯", title: "Mest jagad", unit: ["kort mot sig", "kort mot sig"]},
  {id: "dealt", key: "dealt", icon: "🔥", title: "Delat ut mest", unit: ["klunk", "klunkar"]},
  {id: "knockouts", key: "knockouts", icon: "🥊", title: "Utslagaren", unit: ["utslagen", "utslagna"]},
  {id: "minion-kills", key: "minionKills", icon: "🪓", title: "Vaktknäckaren", unit: ["vakt", "vakter"]},
  {id: "drawn", key: "drawn", icon: "📚", title: "Kortsamlaren", unit: ["extra kort", "extra kort"]},
  {id: "stolen", key: "stolen", icon: "🧤", title: "Långfingret", unit: ["kort taget", "kort tagna"]},
  {id: "self-sips", key: "selfSips", icon: "🤦", title: "Eget mål", unit: ["klunk från egna kort", "klunkar från egna kort"]},
  {id: "fatigue", key: "fatigue", icon: "🪫", title: "Tom lek", unit: ["klunk av tom lek", "klunkar av tom lek"]},
  {id: "played", key: "played", icon: "🃏", title: "Flitigast", unit: ["kort spelat", "kort spelade"]},
  {id: "summoned", key: "summoned", icon: "🧱", title: "Byggherren", unit: ["vakt", "vakter"]},
  {id: "skipped", key: "skipped", icon: "🐌", title: "Sölkorven", unit: ["tur överhoppad", "turer överhoppade"]},
];

// "Ann", "Ann & Bo", "Ann, Bo & Cim", and past three a count: a shared award at a big
// table must still fit on its mat.
function whoText(names) {
  if (names.length > 3) return `${names.slice(0, 2).join(", ")} & ${names.length - 2} till`;
  if (names.length < 2) return names.join("");
  return `${names.slice(0, -1).join(", ")} & ${names[names.length - 1]}`;
}

// Every award somebody actually earned. One nobody did anything for (nothing stolen)
// or that everyone shares (all drank the same) says nothing, so it is left out.
export function matchAwards(players, limit = AWARDS.length) {
  const seats = Array.isArray(players) ? players : [];
  const awards = [];
  for (const award of AWARDS) {
    if (!seats.length) break;
    const values = seats.map((player) => statOf(player, award.key));
    const value = award.best === "min" ? Math.min(...values) : Math.max(...values);
    const holders = seats.filter((_, index) => values[index] === value);
    if (award.best !== "min" && value === 0) continue;
    if (seats.length > 1 && holders.length === seats.length) continue;
    awards.push({
      id: award.id,
      icon: award.icon,
      title: award.title,
      who: whoText(holders.map((player) => String(player.name ?? ""))),
      playerIds: holders.map((player) => player.id),
      value,
      detail: `${value} ${award.unit[value === 1 ? 0 : 1]}`,
    });
  }
  return awards.slice(0, limit);
}

// The player's own numbers for their phone: the basics always, the rest once they happened.
export function statRows(stats) {
  const row = ([key, label]) => ({key, label, value: count(stats?.[key])});
  return [...BASIC_ROWS.map(row), ...EXTRA_ROWS.map(row).filter((entry) => entry.value > 0)];
}

// Fewest sips wins, as in matchOutcome; players on the same count share a place.
export function placementOf(player, players) {
  const seats = Array.isArray(players) ? players : [];
  return 1 + seats.filter((other) => other.sipsTaken < player.sipsTaken).length;
}

// 1:a, 2:a, 3:e … 11:e, 12:e … 21:a, 22:a.
export function ordinal(place) {
  const last = place % 10;
  const teen = place % 100 >= 11 && place % 100 <= 12;
  return `${place}:${!teen && (last === 1 || last === 2) ? "a" : "e"}`;
}
